import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import * as moment from 'moment';
import ReactTable from 'react-table';
import * as Fuse from 'fuse.js';

import 'react-table/react-table.css';

import Backend from './../../Layouts/Backend';
import { deepEqual } from './../../utils';
import SelectionToggle from './../../Shared/UI/SelectionToggle/';
import VehicleCell from './../VehicleCell';
import VehicleRevenueCell from './../VehicleRevenueCell';
import VehicleLastPosition from './../VehicleLastPosition';
import DriverCell from './../../Driver/DriverCell';
import VehicleCurrentFocusCell from './../../VehicleFocus/CurrentFocusCell';

import './styles.css';


const FUSE_OPTIONS = {
  // TEMP: id: '$key',
  // shouldSort: true,

  threshold: 0.2,
  location: 0,
  distance: 100,

  maxPatternLength: 32,
  minMatchCharLength: 1,
};

const INITIAL_STATE = {
  selectedIds: {},
};

class VehiclesPage extends Component {
  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
    this.onToggleAllSelection = this.onToggleAllSelection.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !deepEqual(this.state, nextState)
      || !deepEqual(this.props, nextProps);
  }

  onToggleSingleSelection(id) {
    if (this.state.selectedIds[id]) {
      const selectedIds = { ...this.state.selectedIds };
      delete selectedIds[id];
      this.setState({ selectedIds });
    } else {
      this.setState({
        selectedIds: {
          ...this.state.selectedIds,
          [id]: true,
        },
      });
    }
  }

  onToggleAllSelection() {
    if (Object.keys(this.state.selectedIds).length > 0) {
      this.setState({
        selectedIds: {},
      });
    } else {
      this.setState({
        selectedIds: this.vehicleContextsIds,
      });
    }
  }

  get vehicleContexts() {
    const { vehicles, drivers, fleets } = this.props;
    return _.chain(vehicles)
      .map((vehicle, key) => ({
        vehicle: {
          position: {},
          id: key,
          ...vehicle,
          trip: vehicle.trip ? {
            ...vehicle.trip,
            routeName: `${vehicle.trip.originCity} x ${vehicle.trip.destinationCity}`,
          } : {},
        },
        fleet: !vehicle.fleet ? {} : {
          id: vehicle.fleet,
          ...fleets[vehicle.fleet],
        },
        currentDriver: !vehicle.driver ? {} : {
          id: vehicle.driver,
          ...drivers[vehicle.driver],
        },
      }))
      .value();
  }

  get vehicleContextsIds() {
    return this.getVehicleContextsIds();
  }

  getVehicleContextsIds(contexts = this.vehicleContexts) {
    return _.chain(contexts)
      .map(c => [c.vehicle.id, true])
      .fromPairs()
      .value();
  }


  /** COLUMNS GENERATORS */
  // eslint-disable-next-line react/sort-comp
  genSelectionColumn(data) {
    const { selectedIds } = this.state;
    const contextIds = this.getVehicleContextsIds(data);

    const isAllSelected = deepEqual(contextIds, this.state.selectedIds);
    const isPartiallySelected = Object.keys(this.state.selectedIds).length > 0;

    return {
      id: 'selection',
      accessor: 'vehicle.id',
      width: 50,
      Header: () => (
        <div style={{ marginLeft: '4px' }}>
          <SelectionToggle
            isSelected={isAllSelected}
            isPartial={isPartiallySelected}
            onToggleSelection={this.onToggleAllSelection}
          />
        </div>
      ),
      Cell: props => (
        <div style={{ marginLeft: '4px', height: '44px' }}>
          <SelectionToggle
            isSelected={selectedIds[props.value] === true}
            onToggleSelection={() => this.onToggleSingleSelection(props.value)}
          />
        </div>
      ),
      filterMethod: (filter, row) => {
        if (filter.value === 'all') {
          return true;
        }
        if (filter.value === 'true') {
          return selectedIds[row[filter.id]] === true;
        }
        return selectedIds[row[filter.id]] !== true;
      },
      Filter: ({ filter, onChange }) => (
        <select
          onChange={event => onChange(event.target.value)}
          style={{ width: '100%' }}
          value={filter ? filter.value : 'all'}
        >
          <option value="all" />
          <option value="true">Selecionados</option>
          <option value="false">Não Selecionados</option>
        </select>
      ),
    };
  }

  genVehicleColumn(/* data */) {
    return {
      Header: 'Veículo',
      id: 'vehicle',
      accessor: 'vehicle',
      width: 200,
      Cell: props => (<VehicleCell vehicle={props.value} />),
      sortMethod: (a, b) => (a.plate > b.plate ? 1 : -1),
      filterAll: true,
      filterMethod: (filter, rows) => {
        if (filter.value.length < 2) { return _.orderBy(rows, 'vehicle.plate'); }
        return _.orderBy((new Fuse(rows, {
          ...FUSE_OPTIONS,
          keys: ['vehicle.plate'],
        })).search(filter.value), 'vehicle.plate');
      },
    };
  }

  genTrailerColumn(/* data */) {
    return {
      Header: 'Carreta',
      id: 'trailer',
      accessor: 'vehicle.plate',
      width: 200,
    };
  }

  genCurrendDriverColumn(/* data */) {
    return {
      Header: 'Motorista',
      id: 'currentDriver',
      accessor: 'currentDriver',
      width: 200,
      Cell: props => (<DriverCell driver={props.value} />),
    };
  }

  genRevenueColumn(/* data */) {
    const monthStatsKey = moment().format('YYYYMM');

    return {
      Header: 'Produção',
      id: 'revenue',
      accessor: d => ({
        freightAmount: _.get(d, `vehicle.stats.freightAmount.${monthStatsKey}`, 0),
        lastTrip: _.get(d, 'vehicle.trip', {}),
      }),
      width: 400,
      Cell: props => (<VehicleRevenueCell {...props.value} />),
      sortMethod: (a, b) => (a.freightAmount > b.freightAmount ? 1 : -1),
      filterAll: true,
      filterMethod: (filter, rows) => {
        if (filter.value.length < 2) { return rows; }
        return (new Fuse(rows, {
          ...FUSE_OPTIONS,
          keys: [
            'revenue.lastTrip.routeName',
            'revenue.lastTrip.product',
            'revenue.lastTrip.originDocumentId',
          ],
        })).search(filter.value);
      },
    };
  }

  genLastPositionColumn(/* data */) {
    return {
      Header: 'Última Posição',
      id: 'lastPosition',
      accessor: 'vehicle.position',
      width: 400,
      Cell: props => (<VehicleLastPosition lastPosition={props.value} />),
      sortable: false,
      filterAll: true,
      filterMethod: (filter, rows) => {
        if (filter.value.length < 2) { return rows; }
        return (new Fuse(rows, {
          ...FUSE_OPTIONS,
          keys: [
            'lastPosition.city',
            'lastPosition.detail',
          ],
        })).search(filter.value);
      },
    };
  }

  genCurrentFocusColumn(/* data */) {
    return {
      Header: 'Foco Atual',
      id: 'currentFocus',
      accessor: 'vehicle.currentFocus',
      width: 240,
      Cell: props => (<VehicleCurrentFocusCell currentFocus={props.value} />),
    };
  }

  genNextFocusColumn(index /* , data */) {
    return {
      Header: 'Próx. Foco',
      id: `nextFocus${index}`,
      accessor: 'nextFocuses',
      width: 240,
      Cell: props => (<VehicleCurrentFocusCell currentFocus={props.value} />),
      // pass sequenceCurrentIndex(index) to a new component
    };
  }

  render() {
    const {
      vehicleContexts: data,
    } = this;

    const columns = [
      this.genSelectionColumn(data),
      this.genVehicleColumn(data),
      this.genLastPositionColumn(data),
      // this.genTrailerColumn(data),
      // this.genCurrendDriverColumn(data),
      this.genRevenueColumn(data),
      // this.genCurrentFocusColumn(data),
      // this.genNextFocusColumn(1, data),
      // this.genNextFocusColumn(2, data),
      // this.genNextFocusColumn(3, data),
    ];

    return (
      <Backend>
        <div className="content-inner fixed-width no-padding">
          <ReactTable
            className="vehicle-react-table -highlight"

            // showPagination={false}
            // page={0}
            // pageSize={data.length}
            defaultPageSize={50}

            sortable
            filterable
            defaultSorted={[
              { id: 'vehicle', desc: false },
            ]}

            data={data}
            columns={columns}

            previousText="Anterior"
            nextText="Próxima"
            loadingText="Carregando..."
            noDataText="Nenhum veículo encontrado"
            pageText="Pág"
            ofText="de"
            rowsText="veículos"
          />
        </div>
      </Backend>
    );
  }
}

VehiclesPage.propTypes = {
  vehicles: PropTypes.object.isRequired,
  drivers: PropTypes.object.isRequired,
  fleets: PropTypes.object.isRequired,
};

const mapStateToProps = store => ({
  vehicles: store.vehicleState.vehicles || {},
  drivers: store.driverState.drivers || {},
  fleets: store.fleetState.fleets || {},
});

export default connect(mapStateToProps, null)(VehiclesPage);



// WEBPACK FOOTER //
// ./src/Vehicle/VehiclesPage/index.js